import { useEffect } from 'react';
import { trackContentCardQrScan } from '../models/content/analytics';
import { waitFor } from '../lib/wait-for';

interface QrScanTrackingOptions {
  contentId: number | undefined;
  enabled: boolean;
}

const ATTRIBUTION_PARAMS = ['utm_medium', 'utm_source'];
const PLACEMENT_TOKEN = /^[a-z0-9_]{1,32}$/i;

// Names and values are both matched case-insensitively because some QR encoders
// uppercase the whole URL to reach the smaller alphanumeric encoding mode. An
// unrecognised placement still counts — a signage vendor's typo landing in
// Insights' "Other" bucket beats losing the scan.
export const readQrPlacement = (search: string): string | null => {
  const params: Record<string, string> = {};
  new URLSearchParams(search).forEach((value, key) => {
    params[key.toLowerCase()] = value;
  });

  if (params.utm_medium?.toLowerCase() !== 'qr') return null;

  const placement = params.utm_source?.toLowerCase() || '';
  return PLACEMENT_TOKEN.test(placement) ? placement : null;
};

// The analytics library builds context.campaign from the URL itself, matching
// the "utm_" prefix case-sensitively and passing values through verbatim, so the
// params are rewritten to canonical lowercase before the emit — otherwise an
// uppercased QR URL produces a scan carrying no attribution at all. Rewriting
// also collapses repeated params to one unambiguous pair.
//
// Passing null removes them instead, which is the contract's single-use rule:
// the library re-reads the live query string on every event, and a printed QR
// code gets bookmarked and reopened for months.
const writeAttribution = (placement: string | null) => {
  const url = new URL(window.location.href);
  Array.from(url.searchParams.keys())
    .filter((key) => ATTRIBUTION_PARAMS.includes(key.toLowerCase()))
    .forEach((key) => url.searchParams.delete(key));

  if (placement) {
    url.searchParams.set('utm_medium', 'qr');
    url.searchParams.set('utm_source', placement);
  }

  window.history.replaceState(window.history.state, '', url.toString());
};

const ANALYTICS_LOAD_TIMEOUT_MS = 30000;

// One event per arrival, not per content. The params only leave the URL once the
// emit's callback fires, so without a claim a navigation to different content
// inside that window would emit a second scan off the same query string.
let attributionClaimed = false;

// Test seam: a real page load re-evaluates this module and resets the claim.
export const resetQrArrivalClaim = () => {
  attributionClaimed = false;
};

/**
 * Declare this above any other analytics on the same content load. Effects fire
 * in declaration order, and whichever event reaches the library first is the one
 * that still sees the attribution on the URL. That ordering only holds once the
 * library has loaded, so a sibling event on a cold load may also carry the
 * attribution — Insights slices on the event name, not on source alone.
 */
export const useQrScanTracking = ({
  contentId,
  enabled,
}: QrScanTrackingOptions) => {
  useEffect(() => {
    if (!enabled || !contentId || attributionClaimed) return;

    const placement = readQrPlacement(window.location.search);
    if (!placement) return;

    const emit = () => {
      if (attributionClaimed) return;

      attributionClaimed = true;
      writeAttribution(placement);
      trackContentCardQrScan(contentId, { location: 'content_detail' }, () =>
        writeAttribution(null)
      );
    };

    // Claiming before the library exists burns the arrival: track() drops an
    // event it has waited two seconds for, and the sega bundle is a 338 KB
    // cross-origin fetch. Staying synchronous when it is already there keeps
    // this event ahead of the others on the same load.
    if (window.analytics) {
      emit();
      return;
    }

    waitFor(() => !!window.analytics, ANALYTICS_LOAD_TIMEOUT_MS)
      .then(emit)
      .catch(() => {
        // Never arrived, so nothing was claimed and the parameters are
        // untouched: a later render can still attribute this arrival.
      });
  }, [contentId, enabled]);
};
